import React from 'react';
import ReactDOM from 'react-dom';
import App from './App';
// import '@scuf/common/honeywell-compact-combined/theme.css';
// import '@scuf/datatable/honeywell-compact-combined/theme.css';
import './tailwind.css';
import './index.scss';
import { getCspNonce } from './utils/cspNonce';

import { BrowserRouter, Route, Redirect, Switch } from 'react-router-dom';
import { ActivationPage } from './modules/onboarding/UnauthenticatedPages/ActivationPage';
import authService from './authService';
import { asyncWithLDProvider, useFlags } from 'launchdarkly-react-client-sdk';
import GatewayRegistration from './modules/onboarding/RegistrationManagement/GatewayRegistration';
import { Provider } from 'react-redux';
import rootStore from './modules/commonUtils/Redux/Global/rootStore';
import LoginPage from './pages/LoginPage/LoginPage';
import GenericPage from './pages/GenericPage/GenericPage';
import LoginFailurePage from './pages/LoginFailurePage/LoginFailurePage';
import { ClientProviderWrapper } from './modules/commonUtils/ClientProvider/ClientProviderWrapper';
import { initTooltipViewportClamp } from './utils/tooltipViewportClamp';
// import OnboardingFeature from './modules/onboarding/OnboardingFeature';
// import { OnboardingFeature } from '@bmsp/bmsp.package.web.onboarding';

// // This is a showcase that the onboarding feature is truly able to stand alone, and can exist outside of any app
// ReactDOM.render(
//   // tslint:disable-next-line:jsx-wrap-multiline
//   <BrowserRouter>
//     <OnboardingFeature
//       // authGuard={authGuard}
//       baseURL={authService.BASE_URL}
//       // baseURL={'www.smartnothingatall.com'}
//       // authFetch={fetch}
//       authFetch={authService.authFetch}
//     />
//   </BrowserRouter>,
//   document.getElementById('root')
// );

(window as any).OptanonWrapper = function () {
};

initTooltipViewportClamp();

(async () => {
  await configureCookieForSite();
  const environmentVariables = (window as any).ENV;
  const LDProvider = await asyncWithLDProvider({
    clientSideID: environmentVariables.FEATURE_TOGGLE_CLIENT_ID,
    user: {
      key: 'all_users'
    },
    options: {
      /* ... */
    }
  });

  const AppWithFlags = () => {
    const flags = useFlags();

    return (
      <Provider store={rootStore}>
        <ClientProviderWrapper>
          <BrowserRouter>
            <Switch>
              <Route
                path={[
                  // '/signup/activation/:email/:otp/:organisationId',
                  '/signup/activation/:email/:otp'
                ]}
              >
                <ActivationPage
                  authFetch={authService.authFetch}
                  baseURL={authService.BASE_URL}
                  offering={authService.OFFERING}
                />
              </Route>
              <Route
                path={['/gateway/registration']}
              >
                <GatewayRegistration flags={flags} />
              </Route>
              <Route path="/app">
                <App />
              </Route>
              <Route exact={true} path="/">
                <LoginPage />
              </Route>
              <Route exact={true} path="/mcd">
                <Redirect to={'/app'} />
              </Route>
              <Route exact={true} path="/errors/remote/not-authorized">
                <GenericPage message="User is not authorised" />
              </Route>
              <Route exact={true} path="/errors/remote/device-not-available">
                <GenericPage message="Device is not connected" />
              </Route>
              <Route exact={true} path="/errors/remote/general-error">
                <GenericPage message="Internal Server Error" />
              </Route>
              <Route exact={true} path="/errors/login">
                <LoginFailurePage
                  message="Authentication failed due to a technical error. Please try re-logging by returning to the login page."
                  buttonContent="Retry Login"
                />
              </Route>
              <Route path="*">
                <Redirect to={'/app/not-found'} />
              </Route>
            </Switch>
          </BrowserRouter>
        </ClientProviderWrapper>
      </Provider>
    );
  }

  ReactDOM.render(
    // tslint:disable-next-line:jsx-wrap-multiline
    <LDProvider>
      <AppWithFlags />
    </LDProvider>,
    document.getElementById('root')
  );
})();

async function configureCookieForSite() {
  // Use a flag to track if we've already initialized to prevent duplicate loads
  const ONETRUST_INIT_KEY = 'oneTrustInitialized';
  if (sessionStorage.getItem(ONETRUST_INIT_KEY) === 'true') {
    return;
  }
  const cookieDomainGuid: string = (window as any).ENV.DATA_DOMAIN_SCRIPT_GUID;
  if (!cookieDomainGuid) {
    return;
  }
  const cookieSourceURL: string = "https://cdn.cookielaw.org/consent/" + cookieDomainGuid + "/OtAutoBlock.js";
  const nonce = getCspNonce();
  const nonceAttr: Record<string, string> = nonce ? { nonce } : {};
  // Helper to load script with promise
  const loadScript = (src: string, attributes?: Record<string, string>): Promise<void> => {
    return new Promise((resolve, reject) => {
      // Check if script already exists in DOM
      const existingScript = document.querySelector(`script[src="${src}"]`);
      if (existingScript) {
        resolve();
        return;
      }

      const script = document.createElement('script');
      script.src = src;
      script.type = 'text/javascript';

      if (attributes) {
        Object.entries(attributes).forEach(([key, value]) => {
          script.setAttribute(key, value);
        });
      }

      script.onload = () => {
        resolve();
      };
      script.onerror = (error) => {
        reject(error);
      };

      document.head.appendChild(script);
    });
  };

  try {
    // OptanonWrapper is already defined globally above
    // Load OneTrust SDK stub script and wait for completion
    await loadScript('https://cdn.cookielaw.org/scripttemplates/otSDKStub.js', {
      'data-domain-script': cookieDomainGuid,
      'data-document-language': 'true',
      ...nonceAttr
    });

    // Load auto-block script after SDK is ready
    await loadScript(cookieSourceURL, { ...nonceAttr });
    // Mark as initialized
    sessionStorage.setItem(ONETRUST_INIT_KEY, 'true');
  } catch (error) {
    // Continue loading the app even if cookie scripts fail
  }
}

// async function configureCookieForSite() {
//   const cookieDomainGuid: string = (window as any).ENV.DATA_DOMAIN_SCRIPT_GUID;
//   const cookieSourceURL: string = "https://cdn.cookielaw.org/consent/" + cookieDomainGuid + "/OtAutoBlock.js";

//   var cookieDomainScript = document.createElement('script');
//   cookieDomainScript.src = 'https://cdn.cookielaw.org/scripttemplates/otSDKStub.js';
//   cookieDomainScript.type = 'text/javascript';
//   cookieDomainScript.setAttribute('data-domain-script', cookieDomainGuid);
//   cookieDomainScript.setAttribute('data-document-language', 'true');
//   document.head.append(cookieDomainScript);

//   var cookieSourceScript = document.createElement('script');
//   cookieSourceScript.src = cookieSourceURL;
//   cookieSourceScript.type = 'text/javascript';
//   document.head.append(cookieSourceScript);

//   var cookieFunctionScript = document.createElement('script');
//   cookieFunctionScript.innerText = 'function OptanonWrapper() { }';
//   cookieFunctionScript.type = 'text/javascript';
//   document.head.append(cookieFunctionScript);
// }
